/**
 * PDF for account invoices, rendered through the organisation's own Invoice template
 * (Admin → PDF Export and the document template editor), the same pipeline as other invoices.
 * Only the data differs: the bill-to is the directory account, and quote-only blocks
 * (fees, stages, visual designer) are switched off.
 */
import { buildSharedDocumentCss, paidWatermarkMarkup, STANDARD_DOC_LAYOUT } from '../../shared/pdf/documentPdfStyles';
import { renderDocumentTemplate } from '../../shared/pdf/documentTemplateRenderer';
import { stripTrailingEmptyPdfPagesHtml, withPdfPrintHeightReset } from '../../shared/pdf/prepareExportHtml';
import type { PdfExportConfig } from '../docs/types/pdfExportConfig.types';
import {
  formatInvoiceDateLong,
  invoicePaymentWording,
  paymentTermsLabel,
} from '../docs/utils/invoicePaymentTerms';
import { renderDescriptionHtml } from '../docs/utils/richTextDescription';
import type { DirectoryAccount } from './accounts.model';
import type { AccountInvoiceView } from './accountInvoice.model';
import { money, type BillingArrangement } from './arrangements.model';
import { ACCOUNT_INVOICE_EXPORT_CSS } from './ksuInvoiceTemplate';

function gbp(value: number): string {
  return `£${Number(value || 0).toFixed(2)}`;
}

function escapeHtml(value: unknown): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** The kind of arrangement invoice, read from its source marker. */
function invoiceKind(sourceEvent: string | null | undefined): string {
  const parts = String(sourceEvent || '').split(':');
  return parts[0] === 'arrangement' ? String(parts[2] || '') : '';
}

/** Variables for the Invoice template, filled from an account invoice. */
export function buildAccountInvoiceTemplateData(args: {
  invoice: AccountInvoiceView;
  account: DirectoryAccount | null;
  arrangement: BillingArrangement | null;
  config: PdfExportConfig | null;
}): Record<string, unknown> {
  const { invoice, account, arrangement, config } = args;
  const kind = invoiceKind(invoice.sourceEvent);
  const vatEnabled = invoice.vatTreatment === 'standard';
  const payable = invoice.cisDeduction != null && invoice.cisDeduction > 0
    ? money(invoice.grossTotal - invoice.cisDeduction)
    : invoice.grossTotal;
  const description = renderDescriptionHtml(invoice.notes);
  const accountName = account?.companyName || 'Customer';

  return {
    // Document
    headerTitle: config?.headerTitle || 'Invoice',
    documentType: 'Invoice',
    documentTypeLower: 'invoice',
    documentRef: invoice.invoiceRef,
    documentDate: formatInvoiceDateLong(invoice.issueDate),
    dueDate: formatInvoiceDateLong(invoice.dueDate),
    paymentTermsLabel: paymentTermsLabel(invoice.paymentTermsInDays),
    paymentTermsWording: invoicePaymentWording({ invoiceRole: 'works', paymentTermsInDays: invoice.paymentTermsInDays }),
    documentTitle: invoice.title,
    worksSummary: arrangement?.title || invoice.title,
    description,
    documentArticle: 'an',
    isDepositInvoice: kind === 'deposit',
    isBalanceInvoice: kind === 'balance',
    isVariationInvoice: false,
    depositNarrative: '',
    depositClearedFundsNote: '',
    quotedGross: '',

    // Company (Admin → PDF Export for this organisation)
    companyName: config?.companyName || 'Keystone South UK',
    companyAddress: config?.companyAddress || '',
    companyPhone: config?.companyPhone || '',
    companyEmail: config?.companyEmail || '',
    companyVatNumber: config?.companyVatNumber || '',
    bankName: config?.bankName || '',
    bankAccountName: config?.bankAccountName || '',
    bankAccountNumber: config?.bankAccountNumber || '',
    bankSortCode: config?.bankSortCode || '',
    bankIban: (config as any)?.bankIban || '',
    bankSwift: (config as any)?.bankSwift || '',
    logoPath: config?.logoPath || '',

    // Bill to: the directory account
    customerName: accountName,
    customerFullName: accountName,
    customerTitle: '',
    customerEmail: account?.billingEmail || account?.email || '',
    customerPhone: account?.phone || '',
    customerAddress: [account?.contactName, account?.address]
      .filter(Boolean)
      .map((part) => escapeHtml(part).replace(/\r?\n/g, '<br />'))
      .join('<br />'),

    // Lines, shown ex VAT
    hasLineItems: invoice.lines.length > 0,
    lineItems: invoice.lines.map((line) => ({
      name: escapeHtml(line.name),
      description: line.description ? escapeHtml(line.description) : '',
      quantity: line.qty,
      unit: '',
      unitPrice: gbp(line.qty ? line.net / line.qty : line.net),
      lineTotal: gbp(line.net),
    })),
    hasVisualDesignerItems: false,
    visualDesignerItems: [],

    // Totals
    baseCostTotal: gbp(invoice.netTotal),
    showFees: false,
    adminPct: '0',
    adminFee: gbp(0),
    contingencyPct: '0',
    contingency: gbp(0),
    profitPct: '0',
    profit: gbp(0),
    vatEnabled,
    noVat: !vatEnabled,
    vatPct: String(Math.round(invoice.vatPct * 1000) / 10),
    vatAmount: gbp(invoice.vatAmount),
    grandTotal: gbp(invoice.grossTotal),
    totalExVat: gbp(invoice.netTotal),
    totalIncVat: gbp(invoice.grossTotal),
    amountOutstanding: gbp(payable),
    materialsInclVat: '',
    materialsExcVat: '',
    labourInclVat: '',
    labourExcVat: invoice.cisLabourNet != null ? gbp(invoice.cisLabourNet) : '',
    hasCisDeduction: invoice.cisDeduction != null && invoice.cisDeduction > 0,
    cisDeductionAmount: gbp(invoice.cisDeduction || 0),
    cisLabourAmount: gbp(invoice.cisLabourNet ?? invoice.netTotal),
    arrangementRef: invoice.lifecycleRef,
    arrangementTitle: arrangement?.title || '',
    labourDays: '',
    labourHours: '',
    labourDaysText: '',
    labourHoursText: '',

    // Deposit and stage blocks belong to quotes and workspaces
    hasDeposit: false,
    noDeposit: false,
    depositDisabled: false,
    depositAmount: gbp(0),
    depositPct: '',
    depositPercentage: '',
    depositInvoiceReference: false,
    depositAlreadyInvoicedNarrative: '',
    depositInvoiceAmount: '',
    depositInvoiceRef: '',
    depositInvoicePct: '',
    depositOnly: false,
    outstandingOnly: false,
    stageOnly: false,
    depositExVat: gbp(0),
    depositVatAmount: gbp(0),
    paymentStructurePattern: '',
    contractTotalInclVat: gbp(invoice.grossTotal),
    stageLabel: '',
    stagePct: '',
    stageAmount: gbp(0),
    stageExVat: gbp(0),
    stageVatAmount: gbp(0),
    previouslyInvoicedInclVat: gbp(0),
    remainingAfterThisStageInclVat: gbp(0),
  };
}

function absoluteUrl(pathOrUrl: string | null | undefined, origin: string): string {
  const raw = String(pathOrUrl || '').trim();
  if (!raw) return '';
  if (/^https?:\/\//i.test(raw) || raw.startsWith('data:')) return raw;
  try {
    return new URL(raw, origin).toString();
  } catch {
    return raw;
  }
}

/** Wraps the rendered template exactly as the document page does for its PDF export. */
export function buildAccountInvoiceExportHtml(args: {
  renderedTemplate: string;
  config: PdfExportConfig | null;
  status: string;
  title: string;
  origin: string;
}): string {
  const { renderedTemplate, config, origin } = args;
  const logoPath = String(config?.logoPath || '').trim();
  const exportLogoHtml = logoPath && !renderedTemplate.includes(logoPath)
    ? `<div class="export-logo"><img src="${logoPath}" alt="Company Logo" /></div>`
    : '';
  return `<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(args.title)}</title>
      <base href="${origin}/" />
      <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap" rel="stylesheet" />
      <style>
        ${buildSharedDocumentCss({
          fontFamily: config?.fontFamily || 'Poppins, Arial, sans-serif',
          fontSizeBase: config?.fontSizeBase || 14,
          headerColor: config?.headerColor || '#1976d2',
          primaryColor: config?.primaryColor || '#1976d2',
          secondaryColor: config?.secondaryColor || '#666666',
          logoWidth: config?.logoWidth || 200,
          logoHeight: config?.logoHeight || 100,
          layoutPreset: STANDARD_DOC_LAYOUT,
          useFlexLayout: true,
          includeExportLogo: true,
        })}
        ${ACCOUNT_INVOICE_EXPORT_CSS}
      </style>
    </head><body>
      <main id="main-content">
        ${paidWatermarkMarkup('Invoice', args.status)}
        <div class="our-doc">
          ${exportLogoHtml}
          <div class="content-wrapper">${renderedTemplate}</div>
        </div>
      </main>
    </body></html>`;
}

/** Width (px) of the printable area between the side margins on A4 (794px wide). */
const PAGE_WIDTH_PX = 794;
const SIDE_MARGIN_PX = 36;
const CONTENT_WIDTH_PX = PAGE_WIDTH_PX - SIDE_MARGIN_PX * 2;

export type ImageAspects = {
  /** Width ÷ height of the header image, when known. */
  header?: number | null;
  /** Width ÷ height of the footer image, when known. */
  footer?: number | null;
};

/** Height for an image drawn across the full content width, kept within sensible limits. */
function bandHeight(aspect: number | null | undefined, fallback: number, max: number): number {
  if (!aspect || !Number.isFinite(aspect) || aspect <= 0) return fallback;
  return Math.min(max, Math.max(24, Math.round(CONTENT_WIDTH_PX / aspect)));
}

/**
 * Header and footer images go into Chrome's page header/footer, as for other documents.
 * Each band is sized to its image's proportions so the image spans the full content width;
 * SVG images stay sharp at any size.
 */
export function buildPdfHeaderFooterOptions(config: PdfExportConfig | null, origin: string, aspects: ImageAspects = {}) {
  const headerSrc = absoluteUrl(config?.headerImagePath, origin);
  const footerSrc = absoluteUrl(config?.footerImagePath, origin);
  const side = SIDE_MARGIN_PX;
  const headerTop = 24;
  // Clear space between the header image and the first line of the document.
  const headerGap = 36;
  const footerGap = 14;
  const footerBottom = 18;
  const headerHeight = bandHeight(aspects.header, 130, 160);
  const footerHeight = bandHeight(aspects.footer, 66, 140);
  const image = (src: string, alt: string) => (
    `<img src="${src}" alt="${alt}" style="display:block;width:100%;height:100%;object-fit:contain;object-position:center;" />`
  );
  const headerTemplate = headerSrc
    ? `<div style="width:100%;padding:${headerTop}px ${side}px 0;box-sizing:border-box;"><div style="height:${headerHeight}px;">${image(headerSrc, 'PDF Header')}</div></div>`
    : '';
  const footerTemplate = footerSrc
    ? `<div style="width:100%;padding:0 ${side}px ${footerBottom}px;box-sizing:border-box;"><div style="height:${footerHeight}px;">${image(footerSrc, 'PDF Footer')}</div></div>`
    : '';
  return {
    displayHeaderFooter: !!(headerTemplate || footerTemplate),
    headerTemplate,
    footerTemplate,
    margin: {
      top: `${headerSrc ? headerTop + headerHeight + headerGap : side}px`,
      right: `${side}px`,
      bottom: `${footerSrc ? footerGap + footerHeight + footerBottom : side}px`,
      left: `${side}px`,
    },
  };
}

/** Renders the template and returns the HTML and options for /api/export-pdf. */
export function renderAccountInvoicePdfRequest(args: {
  templateHtml: string;
  invoice: AccountInvoiceView;
  account: DirectoryAccount | null;
  arrangement: BillingArrangement | null;
  config: PdfExportConfig | null;
  origin: string;
  imageAspects?: ImageAspects;
}): { html: string; pdfOptions: ReturnType<typeof buildPdfHeaderFooterOptions> } {
  const renderedTemplate = renderDocumentTemplate({
    templateHtml: args.templateHtml,
    templateData: buildAccountInvoiceTemplateData(args),
    cleanupMode: 'default',
  });
  const html = buildAccountInvoiceExportHtml({
    renderedTemplate,
    config: args.config,
    status: args.invoice.status,
    title: args.invoice.invoiceRef,
    origin: args.origin,
  });
  return {
    html: withPdfPrintHeightReset(stripTrailingEmptyPdfPagesHtml(html.replace(/@page\s*\{[^}]*\}/gi, '@page { size: A4; }'))),
    pdfOptions: buildPdfHeaderFooterOptions(args.config, args.origin, args.imageAspects),
  };
}
